import { Alert, Card, CardBody, CardHeader, CardTitle, H2 } from '@/common'
import { Container } from '@/shared/components'
import { DownloadArrowDownIcon } from '@/common/components/icons'
import useI18n from '@/shared/hooks/useI18n'

const docsBucket = 'https://artem-operator-hk-documentation-0dpdur.oss-cn-hongkong.aliyuncs.com'

type Doc = {
  id: string
  title: string
  url: string
  type: 'video' | 'pdf'
}

const enDocs: Doc[] = [
  {
    id: 'onboarding',
    title: 'Onboarding',
    url: `${docsBucket}/en-GB/Artem%20Operator%20Onboarding.pdf`,
    type: 'pdf'
  },
  {
    id: 'creatingAnInstallationAndProductionRoute',
    title: 'Creating an Installation and Production Route',
    url: `${docsBucket}/en-GB/videos/2-creating-an-installation-and-production-route.mp4`,
    type: 'video'
  },
  {
    id: 'materialManagement',
    title: 'Material Management & Interim Reports creation',
    url: `${docsBucket}/en-GB/videos/3-material-management-interim-reports.mp4`,
    type: 'video'
  },
  {
    id: 'finalisingAReport',
    title: 'Finalising a Report',
    url: `${docsBucket}/en-GB/videos/4-finalising-a-report.mp4`,
    type: 'video'
  },
  {
    id: 'sharingEmissionsData',
    title: 'Sharing Emissions Data',
    url: `${docsBucket}/en-GB/videos/5-sharing-emissions-data.mp4`,
    type: 'video'
  }
]

const chineseDocs: Doc[] = [
  {
    id: 'onboarding-doc',
    title: '入职文档',
    url: `${docsBucket}/zh-CN/artem-operator-onboarding.pdf`,
    type: 'pdf'
  },
  {
    id: 'onboarding',
    title: '入职培训视频',
    url: `${docsBucket}/zh-CN/videos/1-onboarding.mp4`,
    type: 'video'
  }
]

const docsByLanguage = {
  'en-GB': enDocs,
  'zh-CN': chineseDocs
}

function getDocs(language: string): {
  docs: Doc[]
  fullDocumentationNotAvailable: boolean
} {
  if (docsByLanguage[language as keyof typeof docsByLanguage]) {
    const languageDocs = docsByLanguage[language as keyof typeof docsByLanguage]
    return {
      docs: languageDocs,
      fullDocumentationNotAvailable: languageDocs.length !== enDocs.length
    }
  }
  return {
    docs: enDocs,
    fullDocumentationNotAvailable: true
  }
}

export default function DocsPage() {
  const { t, currentLanguage } = useI18n()
  const { docs, fullDocumentationNotAvailable } = getDocs(currentLanguage)

  return (
    <Container>
      <div className="flex flex-col gap-1">
        <H2>{t('docs:title')}</H2>
      </div>
      <div className="space-y-4 max-w-xl mx-auto">
        {fullDocumentationNotAvailable && (
          <Alert type="info" className="text-blue-400">
            {t('docs:fullDocumentationNotAvailable')}
          </Alert>
        )}
        <div className="space-y-4">
          {docs.map(doc => (
            <Card key={doc.url}>
              <CardHeader>
                <CardTitle title={doc.title} />
              </CardHeader>
              <CardBody>
                {doc.type === 'video' ? (
                  <video className="w-full" src={doc.url} controls />
                ) : (
                  <a
                    className="text-blue-400 flex items-center gap-2 hover:underline"
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <DownloadArrowDownIcon className="h-5" />
                    {t('docs:downloadButton', { title: doc.title })}
                  </a>
                )}
              </CardBody>
            </Card>
          ))}
        </div>
      </div>
    </Container>
  )
}
